import { DUPE_API_URL } from '@dupe/common/constants'
import { useQuery } from '@tanstack/react-query'
import useAuthUser from '~/src/hooks/useAuthUser'
import useGetTempUserId from '~/src/hooks/useGetTempUserId'
import fetcher from '~/src/lib/fetcher'

// Unified recent item that supports both dupes and chats

export enum UnifiedRecentItemType {
  Dupe = 'dupe',
  Chat = 'chat',
}

export interface UnifiedRecentItem {
  id: string
  type: UnifiedRecentItemType
  title: string
  image?: string
  url: string
  timestamp: Date
  // For dupes
  hash?: string
  source?: string
  // For chats
  threadId?: string
  summary?: string
  slug?: string
  images?: string[]
}

export type RecentsResponse = {
  recents: UnifiedRecentItem[]
  total: number
}

interface Props {
  limit?: number
  enabled?: boolean
  orderBy?: 'asc' | 'desc'
  includeTotal?: boolean
  includeChats?: boolean
  types?: ('dupe' | 'chat')[]
}

const useFetchRecents = (props?: Props) => {
  const {
    limit = 10,
    enabled = true,
    orderBy = 'desc',
    includeTotal = false,
    includeChats = true,
    types,
  } = props || {}
  const { tempUserId } = useGetTempUserId()
  const { authUser, loading: loadingAuthUser } = useAuthUser()
  const fetchUrl = `${DUPE_API_URL}/api/recents`

  const id = authUser?.uid || tempUserId
  const loading = loadingAuthUser

  return useQuery<RecentsResponse, Error>({
    queryKey: [
      'recents',
      id,
      limit,
      orderBy,
      includeTotal,
      includeChats,
      types,
      authUser?.uid,
      tempUserId,
    ],
    queryFn: () =>
      fetcher<RecentsResponse>(fetchUrl, {
        method: 'GET',
        dataOrParams: {
          limit,
          orderBy,
          includeTotal,
          includeChats,
          types,
        },
        tempUserId,
        authUser,
      }),
    enabled: !!id && enabled && !loading,
  })
}

export default useFetchRecents
